import React, { useEffect, useState } from 'react';
import { useMatch } from 'react-location';
import { useTranslation } from 'react-i18next';
import Page from 'Core/components/Page';
import Loader from 'Core/components/Loader';
import Card from 'Core/components/FormElements/Card';
import { AlertError, AlertWarning } from 'Core/components/Alert';
import {
  useCheckout,
  usePaymentMethods,
  useRefreshCheckout,
  useSelectPaymentMethod,
  useSubmitConsent,
} from 'Modules/checkout/hooks/useCheckout';
import { linkFor, messageOf, rememberLink, statusOf } from 'Modules/checkout/api';
import { checkoutDisplayPath } from 'Modules/checkout/paths';
import { useStripeReturn } from 'Modules/checkout/hooks/useStripeReturn';
import AmountBar from 'Modules/checkout/components/AmountBar';
import SelectPaymentMethod from 'Modules/checkout/components/SelectPaymentMethod';
import NextStep from 'Modules/checkout/components/NextStep';
import Greeting from 'Modules/checkout/components/Greeting';

const FINAL = ['COMPLETED', 'CANCELLED', 'EXPIRED', 'NO_ACTION_PAID', 'PAYMENT_PROCESSING'];

const canChangeMethod = ({ session, next }) =>
  session.selected_payment_method &&
  session.selected_payment_method.lock !== 'LOCKED' &&
  !FINAL.includes(next.next_action);

export default function Checkout() {
  document.title = 'Payment Portal';
  const { t } = useTranslation();
  const { params } = useMatch();
  const linkToken = linkFor(params.linkToken);
  useEffect(() => {
    if (linkToken && params.linkToken === linkToken) {
      window.history.replaceState(null, '', checkoutDisplayPath(rememberLink(linkToken)));
    }
  }, [linkToken, params.linkToken]);
  const { data: checkout, isLoading, isError, error } = useCheckout(linkToken);
  const [choosing, setChoosing] = useState(false);
  const selecting = choosing || checkout?.next.next_action === 'SELECT_PAYMENT_METHOD';
  const options = usePaymentMethods(linkToken, Boolean(checkout) && selecting);
  const mutation = useSelectPaymentMethod(linkToken);
  const consentMutation = useSubmitConsent(linkToken);
  const refresh = useRefreshCheckout(linkToken);
  useStripeReturn(refresh);
  const pageTitle = t('checkout.page.title', 'Zahlung');

  if (isLoading || (selecting && options.isLoading)) {
    return <Loader message={t('status.loading', 'Wird geladen …')} />;
  }
  if (isError) {
    const gone = statusOf(error) === 410;
    return (
      <Page title={pageTitle} withStepProgress={false} hideAlert withClaim={false} compact>
        <Card className="border-solid border-gray-300 shadow-md">
          {gone ? (
            <AlertWarning data-testid="link-expired">
              {t(
                'checkout.linkExpired',
                'Dieser Link ist abgelaufen. Links sind sieben Tage gültig. Bitten Sie Ihren Berater um einen neuen Link.'
              )}
            </AlertWarning>
          ) : (
            <AlertError data-testid="link-invalid">
              {t('checkout.linkInvalid', 'Dieser Link ist nicht mehr gültig.')} {messageOf(error)}
            </AlertError>
          )}
        </Card>
      </Page>
    );
  }

  const { session, next } = checkout;
  const title = pageTitle;
  const wrappedMutation = {
    ...mutation,
    mutate: (body) => mutation.mutate(body, { onSuccess: () => setChoosing(false) }),
  };

  return (
    <Page title={title} withStepProgress={false} hideAlert withClaim={false} compact>
      <div className="max-w-3xl mx-auto">
        <AmountBar
          payment={session.payment}
          title={t('checkout.amount.title', 'Ihre Zuzahlung')}
          onChangeMethod={!selecting && canChangeMethod(checkout) ? () => setChoosing(true) : null}
        />
        <Greeting customer={session.customer} selecting={selecting} />
        {selecting ? (
          <SelectPaymentMethod
            options={options.data?.options || []}
            selected={session.selected_payment_method}
            mutation={wrappedMutation}
          />
        ) : (
          <NextStep
            next={next}
            session={session}
            consentMutation={consentMutation}
            redirectNow={consentMutation.isSuccess}
            onRefresh={refresh}
          />
        )}
      </div>
    </Page>
  );
}
